/**
 * Do I Qualify — results copy matrix
 *
 * Source: DMC-10795 "List copies for all states" (Aurélien).
 *
 * SHAPE: Aurélien organised the copy as four TEMPLATES plus a state→template
 * mapping, rather than per-state copy. This file mirrors that structure
 * literally, so a change to the ticket maps 1:1 onto a change here:
 *
 *   Template A — full matrix (chronic pain / anxiety / PTSD / cancer / default)
 *   Template B — "A, but replace the Anxiety row"   (doctor-discretion wording)
 *   Template C — "A, but delete the Anxiety row"    (anxiety doesn't qualify)
 *   Template D — own copy; no telehealth claims     (FL, AR: new patients in person)
 *   Template E — Group 1 discretion states          (NY, OK, VA, ME, DC)
 *
 * Template E is NOT from DMC-10795 — it comes from the 2026-07-17 update to
 * DMC-10802, which supersedes DMC-10795 for Group 1. It is a single line covering
 * every age and lane, because those states have no checklist and therefore always
 * resolve to the `default` lane.
 *
 * B and C are DERIVED from A on purpose. If A's PTSD copy changes, it changes
 * everywhere — which is what the ticket means by "everything else identical".
 *
 * Resolution order for a given (state, age, condition lane):
 *   STATE_COPY_OVERRIDES[state] → TEMPLATE[state's template] → that age's `default`
 */

import type { ConditionId } from './do-i-qualify-states';
import { getStateConfig } from './do-i-qualify-states';

/** Age buckets. Must match the strings in do-i-qualify-states. */
export type AgeKey = 'Under 18' | '18-20' | '21-34' | '35-64' | '65+';

/** The copy "lanes" the matrix supports. */
export type MatrixConditionKey =
  | 'chronic-pain'
  | 'anxiety'
  | 'ptsd'
  | 'cancer'
  | 'default';

export type TemplateKey = 'A' | 'B' | 'C' | 'D' | 'E';

/**
 * Copy for one age bucket. `default` is required — it's the fallback for any
 * lane this template doesn't define (which is how Template C's missing anxiety
 * row resolves without a special case).
 */
type LaneCopy = Partial<Record<MatrixConditionKey, string>> & {
  default: string;
};

type TemplateCopy = Record<AgeKey, LaneCopy>;

/**
 * Which canonical condition ids map onto which copy lane.
 * Replaces V1's fuzzy substring matching — ids are stable, labels are not.
 */
const LANE_BY_CONDITION: Partial<Record<ConditionId, MatrixConditionKey>> = {
  'chronic-pain': 'chronic-pain',
  'intractable-pain': 'chronic-pain',
  anxiety: 'anxiety',
  // Missouri reaches anxiety only through this statutory wrapper; Template B's
  // "your doctor decides" copy is the right fit for it.
  // TODO(maria): confirm with Aurélien that MO's psychiatric route = anxiety lane.
  'psychiatric-disorders': 'anxiety',
  ptsd: 'ptsd',
  cancer: 'cancer',
};

/** If a user selects several conditions, the highest-priority lane wins. */
const LANE_PRIORITY: MatrixConditionKey[] = [
  'chronic-pain',
  'anxiety',
  'ptsd',
  'cancer',
];

/** Cancer copy is one cell spanning all adult ages (and 18–20, "same as adult"). */
const CANCER_ADULT =
  'Our doctors understand that managing symptoms and quality of life is what matters most. Your appointment is private and handled with care — certification from home, same-day.';

/** Cancer copy for Template D — guide-through framing, no telehealth claim. */
const CANCER_ADULT_D =
  'Our doctors understand that managing symptoms and quality of life is what matters most. Your appointment is private and handled with care, and we’ll guide you through each step. Your information is always private and secure.';

// ── TEMPLATE A ──────────────────────────────────────────────────────────────
// States: PA, NY, NJ, LA, NM, VA, NV, CA, ME
const TEMPLATE_A: TemplateCopy = {
  '21-34': {
    'chronic-pain':
      'Chronic pain qualifies in {state}, and a Leafwell doctor can certify you same-day, 100% online.',
    anxiety:
      'Anxiety qualifies in {state}, and your appointment is fully confidential. A Leafwell doctor can certify you same-day, 100% online.',
    ptsd: 'PTSD is a recognized qualifying condition in {state}. Our doctors know it well — discreet, same-day appointment, no in-person visit.',
    cancer: CANCER_ADULT,
    default:
      'A Leafwell doctor will review your situation and can certify you same-day — 100% online.',
  },
  '35-64': {
    'chronic-pain':
      'Our doctors treat chronic pain as a real, ongoing medical condition — not a formality. Same-day certification, fully online.',
    anxiety:
      '{state} recognizes anxiety as a qualifying condition. A licensed Leafwell doctor will review your case carefully — privately, online.',
    ptsd: 'Our doctors are experienced working with PTSD patients and take it seriously. If you qualify, they can certify you today — no referral needed.',
    cancer: CANCER_ADULT,
    default:
      'A Leafwell doctor will review your conditions carefully and, if you qualify, can certify you same-day — 100% online.',
  },
  '65+': {
    'chronic-pain':
      "You've been managing chronic pain for a while — this part should be simple. A Leafwell physician will review your history and can certify you today, from home.",
    anxiety:
      'You deserve a doctor who takes anxiety seriously. Our physicians are thorough and unhurried — your appointment is private, and certification can happen today.',
    ptsd: 'Our doctors approach PTSD with care and experience. They’ll walk you through the appointment at your pace — privately, online, no in-person visit.',
    cancer: CANCER_ADULT,
    default:
      'A Leafwell physician will take the time to review your situation carefully and can certify you today, from home.',
  },
  '18-20': {
    'chronic-pain':
      'Chronic pain qualifies in {state} starting at 18. A Leafwell doctor can certify you same-day — fully online.',
    anxiety:
      "Anxiety qualifies in {state}, and you're eligible starting at 18. Your appointment is fully confidential — same-day, 100% online.",
    ptsd: 'PTSD qualifies in {state} at any age. Your appointment is private and handled with care — same-day, no in-person visit.',
    cancer: CANCER_ADULT, // ticket: "Same as adult cancer copy — no change"
    default:
      "You're eligible to apply in {state} starting at 18. A Leafwell doctor will review your situation and can certify you same-day — 100% online.",
  },
  'Under 18': {
    'chronic-pain':
      'A parent or a guardian is involved as your caregiver. A Leafwell doctor will work with you both — privately, and certification can happen today.',
    anxiety:
      'A Leafwell doctor will work with you and a parent or a guardian in a fully private, same-day appointment.',
    ptsd: 'A Leafwell doctor will work with you and a parent or a guardian — privately and carefully, at your pace.',
    cancer:
      'Our doctors understand that what matters most is quality of life and symptom management. A Leafwell physician will work with you and a parent or a guardian to complete certification today, from home.',
    default:
      'In {state}, minors can qualify with a parent or guardian as caregiver. A Leafwell doctor will explain your options and can complete certification today.',
  },
};

// ── TEMPLATE B ──────────────────────────────────────────────────────────────
// "Same as Template A, but replace the Anxiety row." States: MA, MD, MO, DE.
//
// NOTE: the ticket's adult anxiety copy is one run-on paragraph, not three
// table cells. Split below across 21–34 / 35–64 / 65+ to mirror A's structure.
// TODO(maria): have Aurélien confirm this split is what he meant.
const TEMPLATE_B_ANXIETY: Partial<Record<AgeKey, string>> = {
  '21-34':
    'In {state}, your certifying doctor decides — anxiety may qualify. Your appointment is fully confidential and 100% online.',
  '35-64':
    "{state} leaves qualifying conditions to your doctor's judgment, so anxiety may qualify. A Leafwell doctor will review your case carefully — privately, online.",
  '65+':
    'You deserve a doctor who takes anxiety seriously. In {state} your doctor decides what qualifies — a private, thorough, unhurried appointment.',
  '18-20':
    "Anxiety may qualify in {state} at your doctor's judgment. You're eligible starting at 18 — a fully confidential, 100% online appointment.",
  'Under 18':
    "Anxiety may qualify at your doctor's judgment. A Leafwell doctor will work with you and a {minorTerm} — privately.",
};

const withAnxietyOverride = (
  base: TemplateCopy,
  anxietyByAge: Partial<Record<AgeKey, string>>,
): TemplateCopy =>
  (Object.keys(base) as AgeKey[]).reduce((acc, age) => {
    acc[age] = {
      ...base[age],
      anxiety: anxietyByAge[age] ?? base[age].anxiety,
    };
    return acc;
  }, {} as TemplateCopy);

const withoutAnxiety = (base: TemplateCopy): TemplateCopy =>
  (Object.keys(base) as AgeKey[]).reduce((acc, age) => {
    const { anxiety: _drop, ...rest } = base[age];
    acc[age] = rest;
    return acc;
  }, {} as TemplateCopy);

const TEMPLATE_B: TemplateCopy = withAnxietyOverride(
  TEMPLATE_A,
  TEMPLATE_B_ANXIETY,
);

// ── TEMPLATE C ──────────────────────────────────────────────────────────────
// "Same as Template A, but delete the Anxiety row."
// States: OH, IL, WV, MN, AZ, HI, CT, MT, VT, TX.
// Anxiety selections (if any) fall through to that age's `default` copy.
const TEMPLATE_C: TemplateCopy = withoutAnxiety(TEMPLATE_A);

// ── TEMPLATE D ──────────────────────────────────────────────────────────────
// States: FL, AR. Only renewals are telehealth, so this template omits every
// "100% online / from home / no in-person visit" claim and uses guide-through
// framing instead. Anxiety row deleted.
//
// The chronic-pain cells for 18–20 and Under 18 are deliberately ABSENT here:
// the ticket gives per-state (FL vs AR) wording, supplied via
// STATE_COPY_OVERRIDES. A new Template D state without an override falls back
// to `default` rather than inheriting the wrong caregiver copy.
const TEMPLATE_D: TemplateCopy = {
  '21-34': {
    'chronic-pain':
      'Chronic pain qualifies in {state}. A Leafwell doctor will review your case and guide you through your evaluation.',
    ptsd: 'PTSD is a qualifying condition in {state}. Our doctors know it well — discreet, and they’ll guide you through each step.',
    cancer: CANCER_ADULT_D,
    default:
      "A Leafwell doctor will review your situation and guide you through {state}'s certification process, step by step.",
  },
  '35-64': {
    'chronic-pain':
      "Our doctors treat chronic pain as a real, ongoing medical condition — not a formality. A Leafwell physician will review your case and guide you through {state}'s process.",
    ptsd: "Our doctors are experienced working with PTSD patients and take it seriously. They’ll review your case and guide you through {state}'s process.",
    cancer: CANCER_ADULT_D,
    default:
      "A Leafwell doctor will review your conditions carefully and, if you qualify, guide you through {state}'s process.",
  },
  '65+': {
    'chronic-pain':
      "You've been managing chronic pain for a while. A Leafwell physician will review your history and walk you through your evaluation, start to finish.",
    ptsd: 'Our doctors approach PTSD with care and experience. They’ll walk you through your evaluation at your pace — privately, start to finish.',
    cancer: CANCER_ADULT_D,
    default:
      'A Leafwell physician will take the time to review your situation and walk you through every step.',
  },
  '18-20': {
    // 'chronic-pain' supplied per-state — see STATE_COPY_OVERRIDES.
    ptsd: "PTSD qualifies in {state}, and you're eligible starting at 18. Your case is handled privately, and we’ll guide you through each step.",
    cancer:
      'Our doctors focus on quality of life and symptom management. Your information is always private and secure, and we’ll guide you through the process.',
    default:
      "You're eligible to apply in {state} starting at 18. A Leafwell doctor will review your situation and guide you through certification.",
  },
  'Under 18': {
    // 'chronic-pain' supplied per-state — see STATE_COPY_OVERRIDES.
    ptsd: 'A Leafwell doctor will work with you and a parent or caregiver — privately and carefully, at your pace.',
    cancer:
      'Our doctors understand that what matters most is quality of life and symptom management. A Leafwell physician will work with your family through the process.',
    default:
      'In {state}, minors qualify with a parent or guardian as caregiver. A Leafwell doctor will explain your options.',
  },
};

// ── TEMPLATE E ──────────────────────────────────────────────────────────────
// Group 1: NY, Oklahoma, Virginia, Maine, Washington DC.
// Source: DMC-10802 (2026-07-17 update), which supersedes DMC-10795 here.
//
// These states have no checklist, so every user resolves to the `default` lane —
// one line serves all five ages.
//
// The ticket's line opens "Good news. …", which collided with the screen's own
// "Great News!" headline. Prefix dropped — confirmed with Aurélien 2026-07-17.
//
// Known and accepted: this drops the caregiver guidance that Template A gives
// Under-18 users. Group 1 minors get no caregiver mention.
const GROUP_1_BODY =
  'Any diagnosed condition your provider believes cannabis may help will qualify.';

const TEMPLATE_E: TemplateCopy = {
  '21-34': { default: GROUP_1_BODY },
  '35-64': { default: GROUP_1_BODY },
  '65+': { default: GROUP_1_BODY },
  '18-20': { default: GROUP_1_BODY },
  'Under 18': { default: GROUP_1_BODY },
};

const TEMPLATES: Record<TemplateKey, TemplateCopy> = {
  A: TEMPLATE_A,
  B: TEMPLATE_B,
  C: TEMPLATE_C,
  D: TEMPLATE_D,
  E: TEMPLATE_E,
};

/**
 * State → template.
 *
 * Base assignment is DMC-10795. The 2026-07-17 DMC-10802 update then defined
 * Group 1 (NY, OK, VA, ME, DC) with its own last-screen copy — DMC-10802 wins on
 * conflict, so those five moved from A (or unassigned) to E. That also retires the
 * "Oklahoma has no template" gap.
 */
export const STATE_TEMPLATE: Record<string, TemplateKey> = {
  // Template E — Group 1, provider discretion (DMC-10802 supersedes DMC-10795)
  ny: 'E',
  ok: 'E',
  va: 'E',
  me: 'E',
  dc: 'E',
  // Template A
  pa: 'A',
  nj: 'A',
  la: 'A',
  nm: 'A',
  nv: 'A',
  ca: 'A',
  // Template B
  ma: 'B',
  md: 'B',
  mo: 'B',
  de: 'B',
  // Template C
  ga: 'C',
  oh: 'C',
  il: 'C',
  wv: 'C',
  mn: 'C',
  az: 'C',
  hi: 'C',
  ct: 'C',
  mt: 'C',
  vt: 'C',
  tx: 'C',
  // Template D
  fl: 'D',
  ar: 'D',
};

/**
 * Per-cell copy exceptions layered on top of a state's template.
 *
 * ⚠️ SUSPECTED TICKET ERROR: DMC-10795 gives the SAME caregiver copy for the
 * 18–20 and Under-18 chronic-pain cells in Template D. An 18–20 patient is a
 * legal adult and does not need a registered caregiver — and the neighbouring
 * 18–20 PTSD cell correctly says "you're eligible starting at 18", which
 * suggests the chronic-pain cell was pasted down a row by mistake.
 * Transcribed faithfully below rather than silently "fixed".
 * TODO(maria): confirm with Aurélien, then delete the '18-20' entries here so
 * they fall back to Template D's adult chronic-pain copy.
 */
export const STATE_COPY_OVERRIDES: Record<
  string,
  Partial<Record<AgeKey, Partial<Record<MatrixConditionKey, string>>>>
> = {
  fl: {
    '18-20': {
      'chronic-pain':
        'A parent or legal guardian can act as your registered caregiver. A Leafwell doctor will work with your family and guide you through each step — privately.',
    },
    'Under 18': {
      'chronic-pain':
        'A parent or legal guardian can act as your registered caregiver. A Leafwell doctor will work with your family and guide you through each step — privately.',
    },
  },
  ar: {
    '18-20': {
      'chronic-pain':
        'A parent or legal guardian acts as your caregiver and must be present at your consultation. A Leafwell doctor will work with your family and guide you through each step — privately.',
    },
    'Under 18': {
      'chronic-pain':
        'A parent or legal guardian acts as your caregiver and must be present at your consultation. A Leafwell doctor will work with your family and guide you through each step — privately.',
    },
  },
};

/** One row of the "What happens next?" list on the results screen. */
export type NextStep = { title: string; subtitle: string };

/**
 * "What happens next?" — the results-screen list.
 *
 * These are DMC-10795's value props restructured per the V2 design: the promo
 * moved into its own box, and "1M+ patients have already trusted Leafwell" is new.
 *
 * ⚠️ Step 2's subtitle is the telehealth claim. Template D states (FL, AR) require
 * new patients in person, so it would be FALSE there — DMC-10795 is explicit that
 * D omits every telehealth claim. Hence two variants.
 */
const NEXT_STEPS_TELEHEALTH: NextStep[] = [
  { title: 'Schedule your consultation', subtitle: 'Takes only minutes' },
  {
    title: 'Meet with a certified doctor',
    subtitle: '100% Online — no in-person visit needed',
  },
  {
    title: 'Receive your certification',
    subtitle: '1M+ patients have already trusted Leafwell',
  },
  {
    title: 'Complete state registration',
    subtitle: 'Money back if not approved',
  },
];

const NEXT_STEPS_IN_PERSON: NextStep[] = [
  { title: 'Schedule your consultation', subtitle: 'Takes only minutes' },
  {
    title: 'Meet with a certified doctor',
    // TODO(maria): Aurélien never specified D's replacement. Proposed.
    subtitle: 'We guide you through every step',
  },
  {
    title: 'Receive your certification',
    subtitle: '1M+ patients have already trusted Leafwell',
  },
  {
    title: 'Complete state registration',
    subtitle: 'Money back if not approved',
  },
];

/**
 * The cancer lane swaps step 1's "Takes only minutes" subtitle for a privacy
 * reassurance. Not applied to Template D: its cancer body copy already ends with
 * "Your information is always private and secure", so it would say it twice.
 * TODO(maria): ask Aurélien whether to keep it in D's body or in the list.
 */
const CANCER_VALUE_PROP_SWAP: Record<TemplateKey, boolean> = {
  A: true,
  B: true,
  C: true,
  D: false,
  E: true, // unreachable — Group 1 has no checklist, so the lane is always default
};

/** Default wording for {minorTerm} (Template B's Under-18 anxiety cell). */
export const DEFAULT_MINOR_TERM = 'parent or guardian';

/** Contextual note shown on the AGE step when a user selects "Under 18". */
export const UNDER_18_NOTE =
  "In many states, minors can qualify with a parent or guardian's support. A Leafwell doctor can walk you through the options available in {state}.";

/** Resolve which copy lane a set of selected conditions maps to. */
export const getMatrixCondition = (
  conditionIds: ConditionId[],
): MatrixConditionKey => {
  const lanes = new Set(
    conditionIds.map(id => LANE_BY_CONDITION[id]).filter(Boolean),
  );
  return LANE_PRIORITY.find(lane => lanes.has(lane)) ?? 'default';
};

export const getStateTemplate = (stateCode: string): TemplateKey =>
  STATE_TEMPLATE[stateCode?.toLowerCase()] ?? 'A';

export type QualifyResult = {
  /** Fixed celebratory line ("Great News!"). */
  headline: string;
  /** Green confidence line — varies on whether anything was selected. */
  subheadline: string;
  /** Matrix body copy, state interpolated. */
  body: string;
  /** "What happens next?" list. */
  nextSteps: NextStep[];
  /** Chosen lane — useful for experiment analysis. */
  matrixCondition: MatrixConditionKey;
  /** Template used — useful for experiment analysis. */
  template: TemplateKey;
};

const interpolate = (
  text: string,
  vars: { state: string; minorTerm: string },
) =>
  text
    .replace(/\{state\}/g, vars.state || 'your state')
    .replace(/\{minorTerm\}/g, vars.minorTerm);

/** Build everything the results screen needs. */
export const getQualifyResult = ({
  stateCode,
  ageRange,
  conditionIds,
  promoCode,
  minorTerm = DEFAULT_MINOR_TERM,
}: {
  stateCode: string;
  ageRange: string | null;
  conditionIds: ConditionId[];
  promoCode: string;
  minorTerm?: string;
}): QualifyResult => {
  const config = getStateConfig(stateCode);
  const stateName = config?.name ?? '';
  const template = getStateTemplate(stateCode);
  const ageKey = (ageRange as AgeKey) ?? '21-34';
  const lane = getMatrixCondition(conditionIds);

  const templateCopy = TEMPLATES[template];
  const ageCopy = templateCopy[ageKey] ?? templateCopy['21-34'];
  const override =
    STATE_COPY_OVERRIDES[stateCode?.toLowerCase()]?.[ageKey]?.[lane];

  // override → template's lane → that age's default
  const bodyTemplate = override ?? ageCopy[lane] ?? ageCopy.default;

  const baseSteps = config?.inPersonRequired
    ? NEXT_STEPS_IN_PERSON
    : NEXT_STEPS_TELEHEALTH;

  const nextSteps =
    lane === 'cancer' && CANCER_VALUE_PROP_SWAP[template]
      ? baseSteps.map(s =>
          s.subtitle === 'Takes only minutes'
            ? {
                ...s,
                subtitle: 'Your information is always private and secure',
              }
            : s,
        )
      : baseSteps;

  return {
    headline: 'Great News!',
    subheadline:
      conditionIds.length > 0 ? 'You likely qualify' : 'You may qualify',
    body: interpolate(bodyTemplate, { state: stateName, minorTerm }),
    nextSteps,
    matrixCondition: lane,
    template,
  };
};
